/* eslint-disable no-param-reassign */
/* ts-disable @typescript-eslint/no-floating-promise */
/* eslint-disable no-shadow */
import React from 'react';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import { useCurrentUserProfile } from '../../app/hooks/useCurrentUserProfile';
import { AppointmentAvailability, Booking } from '../../firebase/firestore/documents/appointmentAvaiability';
import CalendarPresenter from './calendarPresenter';
import { useStoreActions, useStoreState } from '../../models/hooks';
import useCandidates, { CandidatesRenderedByEnum } from './useCandidates';
import { Recruiter } from '../../firebase/firestore/documents/recruiter';
import { useAvailabilityWatcher } from './useAvailabilityWatcher';

const useCalendar = (
  allRecruiters: Recruiter[],
  selectedRecruiters: string[],
  activeCurrentDateRange: { fromDate: Date; toDate: Date },
  adminView?: boolean,
): { calendarPresenter: CalendarPresenter; isCandidatesLoading: boolean; isAvailabilityLoading: boolean } => {
  const [isLoading, setIsLoading] = React.useState(false);
  const query = React.useRef<{ selectedRecruiters: string[]; fromDateStr: string; toDateStr: string } | null>(null);
  const { i18n } = useTranslation();
  const { currentUserProfile } = useCurrentUserProfile();
  const { setCalendarPresenter } = useStoreActions((a) => a.calendar);

  const fromDateStr = activeCurrentDateRange.fromDate.toISOString();
  const toDateStr = activeCurrentDateRange.toDate.toISOString();

  const { candidates, isLoading: isCandidatesLoading } = useCandidates(
    activeCurrentDateRange,
    CandidatesRenderedByEnum.USE_CALENDAR,
  );
  const isOperator = useStoreState((s) => s.app.user?.operator?.enabled);
  const accessibleAccounts = useStoreState((s) => s.app.user?.operator?.accessibleAccounts);
  const defaultAccount = useStoreState((s) => s.app.user?.operator?.defaultAccount);
  const [availableAppointments, setAvailableAppointments] = React.useState<AppointmentAvailability[]>([]);

  const recruiters = allRecruiters?.filter((r) => r?.anonymousUser !== true && r?.firstName !== 'anonymous');

  const fetchAvailabilityByRecruiters = useStoreActions((state) => state.calendar.fetchAvailabilityByRecruiters);

  const fetchAppointments = React.useCallback(
    (isRefetch = false) => {
      if (selectedRecruiters.length > 0) {
        const currentQuery = {
          selectedRecruiters,
          fromDateStr,
          toDateStr,
        };

        // istanbul ignore next
        if (!isRefetch && isEqual(query.current, currentQuery)) {
          return;
        }
        query.current = currentQuery;
        setIsLoading(true);

        fetchAvailabilityByRecruiters({
          userIds: selectedRecruiters,
          from: new Date(fromDateStr),
          to: new Date(toDateStr),
        })
          .then((res) => {
            const result = res.data.data?.map((a) => ({
              ...a,
              createdAt: new Date(a.createdAt),
              fromDate: new Date(a.fromDate),
              toDate: new Date(a.toDate),
              bookings: a?.bookings
                ? Object.entries(a?.bookings)?.reduce((acc, [key, booking]) => {
                    booking.createdAt = new Date(booking.createdAt);
                    booking.date = new Date(booking.date);
                    acc[key] = booking;
                    return acc;
                  }, {} as { [key: string]: Booking })
                : /* istanbul ignore next */ {},
            }));
            // istanbul ignore next
            if (!isEqual(result, availableAppointments)) {
              setAvailableAppointments(result);
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRecruiters, fromDateStr, toDateStr, fetchAvailabilityByRecruiters],
  );

  const handleWatcherChange = React.useCallback(
    (isFirstFetch: boolean) => {
      if (!isFirstFetch) {
        fetchAppointments(true);
      }
    },
    [fetchAppointments],
  );

  useAvailabilityWatcher({
    selectedRecruiters,
    dateRange: {
      fromDate: new Date(fromDateStr),
      toDate: new Date(toDateStr),
    },
    onChange: handleWatcherChange,
  });

  React.useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  return React.useMemo(() => {
    // istanbul ignore else
    if (availableAppointments && candidates && currentUserProfile) {
      const mergedAvailableAppointments = availableAppointments;
      let finalAvailableAppointments;

      if (adminView) {
        finalAvailableAppointments = mergedAvailableAppointments;
      } else if (isOperator) {
        finalAvailableAppointments = mergedAvailableAppointments.filter(
          /* istanbul ignore next */ (value) => {
            return (
              accessibleAccounts?.includes(value.accountId) ||
              defaultAccount === value.accountId ||
              value.accountId === currentUserProfile.account
            );
          },
        );
      } else {
        finalAvailableAppointments = mergedAvailableAppointments.filter(
          /* istanbul ignore next */
          (value) => value.accountId === currentUserProfile.account,
        );
      }

      const calendarPresenter = new CalendarPresenter(finalAvailableAppointments, candidates, recruiters, i18n.language);
      setCalendarPresenter(calendarPresenter);
      // istanbul ignore next
      return { calendarPresenter, isCandidatesLoading, isAvailabilityLoading: isLoading };
    }

    // istanbul ignore next
    return {
      calendarPresenter: new CalendarPresenter([], [], [], i18n.language),
      isCandidatesLoading,
      isAvailabilityLoading: isLoading,
    };
  }, [
    availableAppointments,
    recruiters,
    candidates,
    currentUserProfile,
    adminView,
    isCandidatesLoading,
    isLoading,
    isOperator,
    accessibleAccounts,
    defaultAccount,
    i18n.language,
    setCalendarPresenter,
  ]);
};

export default useCalendar;
